import { ReactNode, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { Fab, List, ListItem, ListItemIcon, ListItemText, Stack, Typography, Divider } from '@mui/material'
import { Add, AdminPanelSettings } from '@mui/icons-material'
import { com } from '@eidu/form'
import EntityTypeRepository from '../../io/EntityTypeRepository'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import PermissionError from './PermissionError'
import ListItemButtonWithNavigation from '../../components/input/ListItemButtonWithNavigation'
import LoadingOverlay from '../../components/feedback/LoadingOverlay'
import { iconForEntityTypeKind } from './kindViewRegistry'
import { isAdminUserType } from '../../util/entityType/isAdminUserType'
import useHasGlobalPermissions from '../permission/useHasGlobalPermissions'
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import {
  assetSidebarSections,
  documentSidebarSections,
  entitySidebarSections,
} from '../../components/navigation/SidebarSection'

type EntityTypesPageProps = { entityTypeRepository: EntityTypeRepository }

const EntityTypesPage = ({ entityTypeRepository }: EntityTypesPageProps) => {
  const navigate = useNavigate()

  const {
    hasPermission: hasGlobalPermission,
    error: permissionsError,
    loading: permissionsLoading,
  } = useHasGlobalPermissions(GlobalAction.ManageEntityTypes)

  const entityTypes = entityTypeRepository.useSortedEntityTypes()

  const adminEntityType = entityTypes?.find(
    (type) => type.kind.equals(EntityTypeKind.User) && isAdminUserType(type.name)
  )

  const createTypeItems = (types: readonly EntityType[] | undefined) =>
    types?.map((type) => ({
      id: type.id,
      name: type.name,
      icon: iconForEntityTypeKind(type.kind),
    })) ?? []

  const filterTypesByKind = (kind: EntityTypeKind, excludeIds: Set<string> = new Set()) =>
    createTypeItems(entityTypes?.filter((type) => type.kind.equals(kind) && !excludeIds.has(type.id.toString())))

  type SectionItem = { id: EntityType['id']; name: string; icon: ReactNode }
  type Section = { title: string; items: SectionItem[]; dividerBefore?: boolean }

  const adminItems: SectionItem[] = []
  if (adminEntityType) {
    adminItems.push({
      id: adminEntityType.id,
      name: 'Admin Users',
      icon: <AdminPanelSettings />,
    })
  }

  const excludeAdminIds = adminEntityType ? new Set([adminEntityType.id.toString()]) : new Set<string>()

  const sections: Section[] = []

  const pushSection = (section: Section, forceDivider = true) => {
    if (!section.items.length) return
    const dividerBefore = sections.length === 0 ? false : forceDivider
    sections.push({ ...section, dividerBefore })
  }

  pushSection({ title: 'Administration', items: adminItems }, false)

  const entityItems = entitySidebarSections.map((s) => ({
    title: s.title,
    items: filterTypesByKind(s.kind, s.kind.equals(EntityTypeKind.User) ? excludeAdminIds : new Set()),
  }))
  const visibleEntityItems = entityItems.filter(({ items }) => items.length)
  visibleEntityItems.forEach((section, index) => pushSection(section, index === 0 && sections.length > 0))

  const documentItems = documentSidebarSections.map((s) => ({
    title: s.title,
    items: filterTypesByKind(s.kind),
  }))
  const visibleDocumentItems = documentItems.filter(({ items }) => items.length)
  visibleDocumentItems.forEach((section, index) => pushSection(section, index === 0 ? sections.length > 0 : true))

  const assetItems = assetSidebarSections.map((s) => ({
    title: s.title,
    items: filterTypesByKind(s.kind),
  }))
  const visibleAssetItems = assetItems.filter(({ items }) => items.length)
  visibleAssetItems.forEach((section, index) => pushSection(section, index === 0 ? sections.length > 0 : true))

  if (permissionsLoading) return <LoadingOverlay isOpen />

  if (permissionsError) return <PermissionError missingPermittedActions={[GlobalAction.ManageEntityTypes]} />

  return (
    (hasGlobalPermission(GlobalAction.ManageEntityTypes) && (
      <>
        <Fab
          color="primary"
          aria-label="Create entity type"
          onClick={() => navigate('new', { relative: 'path' })}
          sx={{
            position: 'fixed',
            bottom: (theme) => theme.spacing(2),
            right: (theme) => theme.spacing(2),
          }}
        >
          <Add />
        </Fab>
        <Stack paddingX={3} paddingY={3} spacing={2.5}>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Types
          </Typography>
          {sections.map(({ title, items }) => (
            <Stack key={title} spacing={0.25}>
              <Typography variant="overline" sx={{ letterSpacing: 1.5, fontSize: '0.7rem', lineHeight: 1, pb: 0.25 }}>
                {title}
              </Typography>
              <Divider sx={{ mb: 0.5 }} />
              <List dense disablePadding>
                {items.map(({ name, id, icon }, index) => (
                  <ListItem key={`entity-type-${id}`} disablePadding sx={{ py: index === 0 ? 0 : 0.25 }}>
                    <ListItemButtonWithNavigation path={`/types/${id.toString()}`}>
                      <ListItemIcon sx={{ minWidth: 30 }}>{icon}</ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontSize: '0.9rem' }} primary={name} />
                    </ListItemButtonWithNavigation>
                  </ListItem>
                ))}
              </List>
            </Stack>
          ))}
        </Stack>
      </>
    )) ||
    PermissionError({ missingPermittedActions: [GlobalAction.ManageEntityTypes] })
  )
}

const EntityTypesPageRoute = () => {
  const entityTypeRepository = useContext(EntityTypesContext)
  return <EntityTypesPage entityTypeRepository={entityTypeRepository} />
}

export default EntityTypesPageRoute
